import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'Invoice vs Receipt — What\'s the Difference?',
      'description': 'Invoices and receipts are not the same thing. Here\'s when to use each, what they must contain, and why the difference matters for your records.',
      'url': 'https://www.invilic.com/blog/invoice-vs-receipt',
      'datePublished': '2025-05-20',
      'dateModified': '2025-05-20',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Can an invoice serve as a receipt?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Not on its own. An invoice is a payment request; a receipt is proof of payment. Once the client pays an invoice and you mark it as paid, the paid invoice can act as a combined document. However, for legal and accounting purposes, a receipt is more authoritative proof of payment than a paid invoice stamp.' },
        },
        {
          '@type': 'Question',
          'name': 'Do you need both an invoice and a receipt?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'For B2B (business to business) transactions, an invoice is typically sufficient and a receipt is optional. For retail or B2C transactions, a receipt is the standard document. Many businesses use invoices for all transactions and then send a "payment received" email or a paid invoice copy when payment comes in, which serves as the receipt.' },
        },
        {
          '@type': 'Question',
          'name': 'What is a proforma invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A proforma invoice is a draft or preliminary invoice sent before goods are shipped or services are performed — usually to get approval for the price. It is not a payment demand and not a formal accounting document. Once the work is done, you replace it with a real invoice. Proforma invoices are common in export trade where customs documentation is needed before shipment.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between a tax invoice and a receipt?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A tax invoice (used under GST in India or VAT in the UK/EU) is a legal document that creates a tax liability for the seller and enables the buyer to claim input tax credits. A receipt simply confirms money was received. A tax receipt (sometimes issued by charities or government bodies) is different again — it confirms payment specifically for tax deduction or compliance purposes.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

export default function InvoiceVsReceiptPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice vs Receipt — What's the Difference? | Invilic Blog"
        description="An invoice requests payment. A receipt confirms payment. Here's exactly when to use each, what they must contain, and why mixing them up creates accounting problems."
        canonical="https://www.invilic.com/blog/invoice-vs-receipt"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Basics</span>
          </div>
          <span className="inline-block bg-slate-100 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">Basics</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            Invoice vs Receipt — What's the Difference?
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">May 20, 2025 · 4 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            An invoice is a request for payment — it says "you owe me this amount." A receipt is proof of payment — it says "you paid me this amount." They are two different documents, issued at different points in a transaction, and using them interchangeably will cause problems in your accounting records.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What Is an Invoice?</h2>
          <p className="mb-4">
            An invoice is sent before or immediately after work is complete to request payment from your client. It includes a full breakdown of what was provided and how much is owed. It signals the start of a payment obligation.
          </p>
          <p className="mb-6">
            Invoices are forward-looking documents. When you send one, payment has not yet been made. The invoice sets out the amount, the due date, and the payment method.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What Is a Receipt?</h2>
          <p className="mb-4">
            A receipt is issued after payment has been made. It confirms that money changed hands. A receipt is backward-looking — it documents something that has already happened.
          </p>
          <p className="mb-6">
            Receipts are what customers receive from a shop after buying something. For freelancers and B2B services, a receipt is often a "paid invoice" — the original invoice stamped or marked "PAID" with the payment date.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Side-by-Side Comparison</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800" />
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Invoice</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { aspect: 'Purpose', invoice: 'Request for payment', receipt: 'Proof of payment' },
                  { aspect: 'When issued', invoice: 'Before or when payment is due', receipt: 'After payment is received' },
                  { aspect: 'Issued by', invoice: 'Seller / service provider', receipt: 'Seller / service provider' },
                  { aspect: 'Payment status', invoice: 'Unpaid', receipt: 'Paid' },
                  { aspect: 'Buyer use', invoice: 'Records an obligation', receipt: 'Confirms payment was made' },
                  { aspect: 'Tax purpose', invoice: 'Creates tax liability (VAT/GST)', receipt: 'No direct tax role' },
                ].map(({ aspect, invoice, receipt }) => (
                  <tr key={aspect} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-800">{aspect}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{invoice}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{receipt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <BlogCTA
            text="Need to send a professional invoice? Create one in under 2 minutes — free, no sign-up required."
            to="/create/freelancer"
            btnText="Create Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">When Do You Use Each?</h2>
          <p className="mb-4">
            <strong className="font-semibold text-gray-900 dark:text-white">Send an invoice when:</strong> you have completed work or delivered goods and you want to be paid. Invoice the client before payment is expected.
          </p>
          <p className="mb-4">
            <strong className="font-semibold text-gray-900 dark:text-white">Issue a receipt when:</strong> you receive payment. This can be as simple as sending the original invoice with "PAID" added and the date of payment noted.
          </p>
          <p className="mb-6">
            For most freelancers and small businesses doing B2B work, a paid invoice functions as both an invoice and a receipt. You do not need a separate receipt document — just mark the invoice paid once the payment clears.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Why Getting This Right Matters</h2>
          <p className="mb-4">
            For your accounting: unpaid invoices are accounts receivable (you are owed money). Receipts reduce accounts receivable (money has arrived). Confusing the two results in your accounts showing income you haven't yet collected — or missing income you have.
          </p>
          <p className="mb-6">
            For tax purposes: a tax invoice (under GST or VAT) creates a specific legal document that entitles the buyer to claim input credits. A receipt cannot substitute for a tax invoice in most tax systems.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a professional invoice in seconds — every essential field included, PDF download free."
            to="/free-invoice-generator"
            btnText="Create Free Invoice"
          />

        </article>
        <RelatedArticles current="/blog/invoice-vs-receipt" />
      </main>
    </div>
  );
}
